/**
 * Node customizado para mensagens (texto e mídia)
 * Design com ícone de mensagem e preview visual do conteúdo
 * Inclui toolbar flutuante ao selecionar
 */

import { Handle, Position, NodeProps } from '@xyflow/react';
import { MessageSquare, Image, FileText, Play, Music, Video } from 'lucide-react';
import { FlowNodeData } from '@/types/flows';
import { useState, useMemo } from 'react';
import { FlowNodeToolbar } from './FlowNodeToolbar';
/**
 * Extrai ID do YouTube para gerar thumbnail
 */
const getYouTubeThumbnail = (url: string): string | null => {
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
  return match ? `https://img.youtube.com/vi/${match[1]}/mqdefault.jpg` : null;
};

/**
 * Extrai nome e extensão do arquivo de uma URL
 */
const getFileInfo = (url: string): { name: string; extension: string } => {
  const filename = url.split('/').pop() || 'arquivo';
  const parts = filename.split('.');
  const extension = parts.length > 1 ? parts.pop()!.toUpperCase() : 'FILE';
  const name = parts.join('.');
  return { name, extension };
};

/**
 * Componente de preview de imagem
 */
function ImagePreview({ url }: { url: string }) {
  const [error, setError] = useState(false);

  if (error) {
    return (
      <div className="flex items-center justify-center h-24 bg-muted rounded-md">
        <Image className="h-8 w-8 text-muted-foreground" />
      </div>
    );
  }

  return (
    <img
      src={url}
      alt="Preview"
      className="w-full max-h-[120px] object-cover rounded-md"
      onError={() => setError(true)}
    />
  );
}

/**
 * Componente de preview de vídeo
 */
function VideoPreview({ url }: { url: string }) {
  const [error, setError] = useState(false);
  const thumbnail = getYouTubeThumbnail(url);

  if (error && !thumbnail) {
    return (
      <div className="flex items-center justify-center h-24 bg-muted rounded-md relative">
        <Video className="h-8 w-8 text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="relative w-full">
      {thumbnail ? (
        <img
          src={thumbnail}
          alt="Video preview"
          className="w-full max-h-[120px] object-cover rounded-md"
        />
      ) : (
        <video
          src={url}
          className="w-full max-h-[120px] object-cover rounded-md"
          onError={() => setError(true)}
        />
      )}
      <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-md">
        <div className="bg-red-600 rounded-full p-3">
          <Play className="h-6 w-6 text-white fill-white" />
        </div>
      </div>
    </div>
  );
}

/**
 * Componente de preview de áudio
 */
function AudioPreview() {
  // Gera alturas aleatórias apenas uma vez na montagem do componente
  const waveformHeights = useMemo(() => 
    [...Array(12)].map(() => Math.random() * 16 + 8),
    []
  );

  return (
    <div className="flex items-center gap-3 p-3 bg-muted rounded-md">
      <div className="bg-primary/10 p-2 rounded">
        <Music className="h-6 w-6 text-primary" />
      </div>
      <div className="flex-1">
        <div className="text-xs font-medium text-foreground">Mensagem de Áudio</div>
        <div className="flex gap-1 mt-1">
          {waveformHeights.map((height, i) => (
            <div
              key={i}
              className="w-1 bg-primary/40 rounded-full"
              style={{ height: `${height}px` }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Componente de preview de documento
 */
function DocumentPreview({ url }: { url: string }) {
  const { name, extension } = getFileInfo(url);

  return (
    <div className="flex items-center gap-3 p-3 bg-muted rounded-md">
      <div className="bg-primary/10 p-2 rounded">
        <FileText className="h-6 w-6 text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium text-foreground truncate">{name}</div>
        <div className="inline-block mt-1 px-2 py-0.5 bg-primary/20 text-primary text-[10px] font-semibold rounded">
          {extension}
        </div>
      </div>
    </div>
  );
}

/**
 * Componente visual do node de mensagem
 */
export function MessageNode({ data, id, selected }: NodeProps) {
  const nodeData = data as FlowNodeData;
  const isMedia = nodeData.type === 'message_media';
  
  // Callbacks injetados via FlowCanvas
  const onEdit = (nodeData as any).onEdit;
  const onDuplicate = (nodeData as any).onDuplicate;
  const onDelete = (nodeData as any).onDelete;
  const onTestFromHere = (nodeData as any).onTestFromHere;
  
  // Extrai dados de mídia
  const mediaType = nodeData.config?.media_type;
  const mediaUrl = nodeData.config?.media_url;
  
  // Extrai preview do conteúdo da configuração
  const content = nodeData.config?.text || nodeData.config?.caption || '';
  const preview = content.length > 50 ? content.substring(0, 50) + '...' : content;

  // Define título, ícone e cor baseado no tipo do node
  let title = 'Enviar Mensagem';
  let Icon = MessageSquare;
  let iconColorClass = 'bg-blue-500 text-white';

  if (isMedia) {
    title = 'Mídia';
    Icon = Image;
    iconColorClass = 'bg-purple-500 text-white';
    
    if (mediaType) {
      switch (mediaType) {
        case 'image':
          title = 'Imagem';
          Icon = Image;
          break;
        case 'video':
          title = 'Vídeo';
          Icon = Video;
          break;
        case 'audio':
          title = 'Áudio';
          Icon = Music;
          break;
        case 'document':
          title = 'Documento';
          Icon = FileText;
          break;
      }
    }
  }

  return (
    <div className="bg-card rounded-lg shadow-lg border-2 border-border min-w-[200px] max-w-[280px]">
      {/* Toolbar flutuante */}
      <FlowNodeToolbar
        nodeId={id}
        nodeType={nodeData.type}
        onEdit={onEdit}
        onDuplicate={onDuplicate}
        onDelete={onDelete}
        onTestFromHere={onTestFromHere}
        isVisible={selected}
      />
      
      <Handle
        type="target"
        position={Position.Top}
        className="!bg-border !w-3 !h-3"
      />
      
      <div className="p-3">
        <div className="flex items-center gap-2 mb-2">
          <div className={`${iconColorClass} p-2 rounded`}>
            <Icon className="h-4 w-4" />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-sm text-foreground">
              {nodeData.config?.nickname || title}
            </div>
          </div>
        </div>
        
        {/* Preview de mídia */}
        {isMedia && mediaUrl && (
          <div className="mt-2 mb-2">
            {mediaType === 'image' && <ImagePreview url={mediaUrl} />}
            {mediaType === 'video' && <VideoPreview url={mediaUrl} />}
            {mediaType === 'audio' && <AudioPreview />}
            {mediaType === 'document' && <DocumentPreview url={mediaUrl} />}
          </div>
        )}
        
        {/* Preview de texto/legenda */}
        {preview && (
          <div className="text-xs text-muted-foreground bg-muted/50 rounded p-2 mt-2">
            {preview}
          </div>
        )}
      </div>
      
      <Handle
        type="source"
        position={Position.Bottom}
        className="!bg-border !w-3 !h-3"
      />
    </div>
  );
}
